import {
  updateLabelConfig,
  labelConfigSelector,
  labelPropertiesSelector,
  onLabelPropertyValueChange,
  isLabelPropertyEnabledSelector,
} from '../../../data/labelInfos/index.js'
import ConfigSelect from './ConfigSelect.js'
import {getState, useSelector} from '../../../store.js'
import {
  DHL,
  FEDEX,
  FEDEX_360,
  UPS,
} from '../../../common/constants/ShipperNames.js'
import ConfigTextInput from './ConfigTextInput.js'
import EnableLabelProperty, {PickLabelProperty} from './EnableLabelProperty.js'
import useLabelProperty, {
  ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES_FUNC,
  defaultValidLabelPropertiesFunc,
} from './useLabelProperty.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'

// Type
const VALID_SHIPPER_TYPES = [DHL, FEDEX, FEDEX_360, UPS]

export function getDutiesPaymentTypeLabelProperty(shipperType) {
  return `${shipperType}__${
    shipperType === DHL ? 'duty_payment_type' : 'duties_payment_type'
  }`
}

function labelPropertiesFunc(shipperType) {
  return VALID_SHIPPER_TYPES.includes(shipperType)
    ? [getDutiesPaymentTypeLabelProperty(shipperType)]
    : []
}

// Account
function getDutiesPaymentAccountLabelProperty(shipperType) {
  return `${shipperType}__duties_payment_account`
}
const DUTIES_PAYMENT_ACCOUNT_VALID_LABEL_PROPERTIES = VALID_SHIPPER_TYPES.map(
  getDutiesPaymentAccountLabelProperty,
)

function showDutiesPaymentAccountSelector(state, {labelInfoID, shipperType}) {
  const labelConfig = labelConfigSelector(state, {labelInfoID})
  const [labelProperty] = labelPropertiesFunc(shipperType)

  const paymentType = labelConfig[labelProperty] || ''

  return (
    (paymentType !== 'S' && shipperType === DHL) ||
    (paymentType !== 'SENDER' && [FEDEX, FEDEX_360].includes(shipperType)) ||
    (paymentType !== 'BillShipper' && shipperType === UPS)
  )
}

function dutiesPaymentAccountDependentFunc(shipperType) {
  return [
    ...ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES_FUNC(shipperType),
    ...labelPropertiesFunc(shipperType),
  ]
}

function dutiesPaymentAccountPropertiesFunc(shipperType, labelInfoID) {
  const showDutiesPaymentAccount = showDutiesPaymentAccountSelector(
    getState(),
    {
      labelInfoID,
      shipperType,
    },
  )

  return showDutiesPaymentAccount
    ? [getDutiesPaymentAccountLabelProperty(shipperType)]
    : []
}

// Zip and Country
const DUTIES_ZIP_COUNTRY_VALID_SHIPPER_TYPES = [UPS]
function getDutiesPaymentZipLabelProperty(shipperType) {
  return `${shipperType}__duties_payment_zip`
}
function getDutiesPaymentCountryLabelProperty(shipperType) {
  return `${shipperType}__duties_payment_country`
}
const DUTIES_ZIP_COUNTRY_VALID_LABEL_PROPERTIES = [
  ...DUTIES_ZIP_COUNTRY_VALID_SHIPPER_TYPES.map(
    getDutiesPaymentZipLabelProperty,
  ),
  ...DUTIES_ZIP_COUNTRY_VALID_SHIPPER_TYPES.map(
    getDutiesPaymentCountryLabelProperty,
  ),
]

function showDutiesPaymentZipCountrySelector(
  state,
  {labelInfoID, shipperType},
) {
  const labelConfig = labelConfigSelector(state, {labelInfoID})
  const [labelProperty] = labelPropertiesFunc(shipperType)

  const paymentType = labelConfig[labelProperty] || ''

  return paymentType === 'BillThirdParty' && shipperType === UPS
}

function isDutiesZipCountryPropertiesEnabledSelector(
  state,
  {labelInfoID, shipperType},
) {
  const properties = labelPropertiesSelector(state, {labelInfoID})

  return (
    properties.includes(getDutiesPaymentZipLabelProperty(shipperType)) &&
    properties.includes(getDutiesPaymentCountryLabelProperty(shipperType))
  )
}

function dutiesZipCountryDependentPropertiesFunc(shipperType) {
  return [
    ...ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES_FUNC(shipperType),
    ...labelPropertiesFunc(shipperType),
  ]
}

function dutiesZipCountryLabelPropertiesFunc(shipperType, labelInfoID) {
  const showDutiesPaymentZipCountry = showDutiesPaymentZipCountrySelector(
    getState(),
    {
      labelInfoID,
      shipperType,
    },
  )

  return showDutiesPaymentZipCountry
    ? [
        getDutiesPaymentZipLabelProperty(shipperType),
        getDutiesPaymentCountryLabelProperty(shipperType),
      ]
    : []
}

export function DutiesPayor() {
  const {labelInfoID, shipperType, onChange} = useLabelConfigContext()
  const [propertySpecs] = DutiesPayor.propertySpecList
  const [labelProperty] = propertySpecs.labelPropertiesFunc(
    shipperType,
    labelInfoID,
  )

  return (
    <ConfigSelect
      label="Duties Payor"
      noEmptyOption
      onChange={(value) => {
        updateLabelConfig(labelInfoID, {
          [labelProperty]: value,
        })

        onLabelPropertyValueChange(labelInfoID, labelProperty)

        onChange()
      }}
      labelProperty={labelProperty}
      propertySpecs={propertySpecs}
      shipperOptionsProperty="payor"
    />
  )
}

DutiesPayor.propertySpecList = [
  {
    validShipperTypes: VALID_SHIPPER_TYPES,
    labelPropertiesFunc,
    dependentPropertiesFunc: ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES_FUNC,
    validLabelPropertiesFunc: defaultValidLabelPropertiesFunc,
  },
]

export function DutiesPayorAccountNumber() {
  const {labelInfoID, shipperType, onChange} = useLabelConfigContext()
  const [propertySpecs] = DutiesPayorAccountNumber.propertySpecList

  const [labelProperty] = propertySpecs.labelPropertiesFunc(
    shipperType,
    labelInfoID,
  )

  const showDutiesPaymentAccount = useSelector((state) =>
    showDutiesPaymentAccountSelector(state, {labelInfoID, shipperType}),
  )

  return (
    <ConfigTextInput
      label="Duties Payor Account Number"
      onChange={(value) => {
        updateLabelConfig(labelInfoID, {
          [labelProperty]: value,
        })

        onChange()
      }}
      labelProperty={labelProperty}
      propertySpecs={propertySpecs}
      hide={!showDutiesPaymentAccount}
    />
  )
}

DutiesPayorAccountNumber.propertySpecList = [
  {
    validShipperTypes: VALID_SHIPPER_TYPES,
    labelPropertiesFunc: dutiesPaymentAccountPropertiesFunc,
    dependentPropertiesFunc: dutiesPaymentAccountDependentFunc,
    validLabelPropertiesFunc() {
      return DUTIES_PAYMENT_ACCOUNT_VALID_LABEL_PROPERTIES
    },
  },
]

export function DutiesPayorZipcode() {
  const {
    labelInfoID,
    shipperType,
    onChange,
    isLabelEnableMode,
    isSummary,
    isPickMode,
  } = useLabelConfigContext()
  const [propertySpecs] = DutiesPayorZipcode.propertySpecList
  const [dutiesPaymentZipLabelProperty, dutiesPaymentCountryLabelProperty] =
    propertySpecs.labelPropertiesFunc(shipperType, labelInfoID)

  const labelConfig = useSelector((state) =>
    labelConfigSelector(state, {labelInfoID}),
  )
  const showDutiesPaymentZipCountry = useSelector((state) =>
    showDutiesPaymentZipCountrySelector(state, {labelInfoID, shipperType}),
  )

  useLabelProperty({
    labelProperty: dutiesPaymentZipLabelProperty,
    propertySpecs,
  })

  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: dutiesPaymentZipLabelProperty,
    }),
  )

  const dutiesPaymentZip = labelConfig[dutiesPaymentZipLabelProperty] || ''
  const dutiesPaymentCountry =
    labelConfig[dutiesPaymentCountryLabelProperty] || ''

  if (!showDutiesPaymentZipCountry) {
    return null
  }

  const label = 'Duties Payor Zipcode/Country'

  if (isSummary) {
    return label
  }

  if (isPickMode) {
    return (
      <PickLabelProperty
        label={label}
        labelProperty={dutiesPaymentZipLabelProperty}
      />
    )
  }

  return (
    <li className="list__item--shipping-options fs-n1 margin-bottom-5">
      <div className="flex flex-wrap wrap--edit-preset-form-input">
        <div className="margin-bottom-5 margin-right-15">
          <label className="label--bold" htmlFor="duties-payment-zip">
            Duties Payor Zipcode
          </label>
          <input
            className="input--md inline-block"
            type="text"
            id="duties-payment-zip"
            value={dutiesPaymentZip}
            onChange={(event) => {
              updateLabelConfig(labelInfoID, {
                [dutiesPaymentZipLabelProperty]: event.target.value,
              })

              onChange()
            }}
            disabled={isLabelEnableMode && !isLabelPropertyEnabled}
          />
        </div>
        <div className="margin-bottom-5">
          <label className="label--bold" htmlFor="duties-payment-country">
            Duties Payor Country Code
          </label>
          <input
            className="input--sm inline-block"
            type="text"
            id="duties-payment-country"
            value={dutiesPaymentCountry}
            onChange={(event) => {
              updateLabelConfig(labelInfoID, {
                [dutiesPaymentCountryLabelProperty]: event.target.value,
              })

              onChange()
            }}
            disabled={isLabelEnableMode && !isLabelPropertyEnabled}
          />
        </div>
      </div>
      <EnableLabelProperty
        labelProperty={dutiesPaymentZipLabelProperty}
        checkedSelector={(state) =>
          isDutiesZipCountryPropertiesEnabledSelector(state, {
            labelInfoID,
            shipperType,
          })
        }
      />
    </li>
  )
}

DutiesPayorZipcode.propertySpecList = [
  {
    validShipperTypes: DUTIES_ZIP_COUNTRY_VALID_SHIPPER_TYPES,
    labelPropertiesFunc: dutiesZipCountryLabelPropertiesFunc,
    dependentPropertiesFunc: dutiesZipCountryDependentPropertiesFunc,
    validLabelPropertiesFunc() {
      return DUTIES_ZIP_COUNTRY_VALID_LABEL_PROPERTIES
    },
  },
]
